import { useMutation, useQuery } from 'react-query';

import { ExportStatus } from '@custom-types/Export';

import { createBadgeExport, getBadgeExport } from './service';

type ExportBadgeKey = ['printers', 'export', string /* eventId */, string /* checkInCode */];

export const useExportBadgeMutation = (eventId: string, checkInCode: string, testPrint = false) =>
  useMutation({
    mutationKey: <ExportBadgeKey>['printers', 'export', eventId, checkInCode],
    mutationFn: () =>
      createBadgeExport(eventId, testPrint ? { test: true } : { checkin_code: checkInCode }),
  });

export const useGetBadgeExport = (
  eventId: string,
  id: string | undefined,
  onFinishedExporting: (url: string | undefined, error?: Error) => void
) =>
  useQuery({
    enabled: !!id,
    queryKey: <ExportBadgeKey>['printers', 'export', eventId, id],
    queryFn: () => getBadgeExport(eventId, id!),
    refetchInterval: data => (data?.data.state === ExportStatus.Pending ? 1000 : false),
    onSuccess: data => {
      if (data?.data.state === ExportStatus.Completed) {
        onFinishedExporting(data.data.artifacts[0].url);
      }
      if (data?.data.state === ExportStatus.Failed) {
        // The export does not seem to contain a meaningful error message, so we'll use a generic
        // one. This won't actually be displayed to the user though and is just for developers
        let exportError = '';
        try {
          const exportArgs = JSON.parse(data.data.arguments);
          if (exportArgs.error_message) {
            exportError = `: ${exportArgs.error_message}`;
          }
        } catch {
          // If we couldn't extract the error message, we'll just ignore it
        }
        const error = new Error(`An error occurred while exporting the badge${exportError}`);
        // eslint-disable-next-line no-console
        console.error(error);
        onFinishedExporting(undefined, error);
      }
    },
  });
